import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useCategoryTree } from "@/hooks/useCategoryTree";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const utilityLinks = [
  { label: "Hijab Quiz", href: "/quiz" },
  { label: "Gift Cards", href: "/gift-cards" },
  { label: "New Arrivals", href: "/shop?sort=newest" },
  { label: "Best Sellers", href: "/shop?sort=best-sellers", isCta: true },
];

interface MobileNavMenuProps {
  onClose: () => void;
  onSignOut: () => void;
}

const MobileNavMenu = ({ onClose, onSignOut }: MobileNavMenuProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const { pathname } = useLocation();
  const { user, isVendor, isAdmin, vendorInfo } = useAuth();
  const { publishedTree } = useCategoryTree();

  // Level 1 categories from published tree
  const level1Categories = publishedTree.flatMap((root) =>
    (root.children || []).filter((c) => c.is_active && c.is_published)
  );

  const toggle = (i: number) => setExpandedIndex(expandedIndex === i ? null : i);

  return (
    <nav aria-label="Mobile navigation" className="px-4 py-3 overflow-y-auto h-[calc(100%-65px)]">
      {/* Utility Links */}
      <ul className="flex flex-wrap gap-2 pb-3 mb-3 border-b border-border/50 list-none m-0 p-0">
        {utilityLinks.map((link) => (
          <li key={link.href}>
            <Link
              to={link.href}
              onClick={onClose}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors inline-block ${
                link.isCta
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground hover:bg-muted/80"
              }`}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>

      {/* Sell on HJB */}
      <div className="pb-3 mb-3 border-b border-border/50">
        <Link
          to={user && vendorInfo ? "/vendor/application" : "/vendor/register"}
          onClick={onClose}
          className="inline-block text-sm font-semibold px-4 py-1.5 rounded-full bg-[hsl(4,76%,64%)]/10 text-[hsl(4,76%,64%)] hover:bg-[hsl(4,76%,64%)] hover:text-white transition-all duration-200"
        >
          Sell on HJB
        </Link>
      </div>

      {/* Dynamic Categories */}
      <ul className="list-none m-0 p-0 space-y-0">
        {level1Categories.map((cat, i) => {
          const isExpanded = expandedIndex === i;
          const isActive = pathname.startsWith(`/${cat.slug}`);
          const level2Children = (cat.children || []).filter(
            (c) => c.is_active && c.is_published
          );
          const hasChildren = level2Children.length > 0;

          return (
            <li key={cat.id} className="border-b border-border/30">
              <div className="flex items-center justify-between">
                <Link
                  to={`/${cat.slug}`}
                  onClick={onClose}
                  className="flex-1 text-sm font-semibold py-3.5 transition-colors"
                  style={cat.font_color ? { color: cat.font_color } : undefined}
                >
                  {cat.name}
                  {cat.badge_label && (
                    <span
                      className="ml-1.5 inline-flex items-center justify-center text-[9px] font-bold uppercase leading-none px-1.5 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: cat.badge_color || "hsl(var(--primary))" }}
                    >
                      {cat.badge_label}
                    </span>
                  )}
                </Link>
                {hasChildren && (
                  <button
                    type="button"
                    onClick={() => toggle(i)}
                    aria-expanded={isExpanded}
                    aria-label={`Expand ${cat.name}`}
                    className="p-2 -mr-2"
                  >
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                )}
              </div>

              {isExpanded && hasChildren && (
                <ul className="pb-3 pl-2 list-none m-0 p-0 pb-3 pl-2">
                  {level2Children.map((l2) => {
                    const l3Children = (l2.children || []).filter(
                      (c) => c.is_active && c.is_published
                    );
                    return (
                      <li key={l2.id} className="mb-3">
                        <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 px-2" aria-hidden="true">
                          {l2.name}
                        </span>
                        <ul className="list-none m-0 p-0">
                          {l3Children.map((l3) => (
                            <li key={l3.id}>
                              <Link
                                to={`/${cat.slug}/${l2.slug}/${l3.slug}`}
                                onClick={onClose}
                                className={`block text-sm py-2 px-2 rounded-md transition-colors ${
                                  pathname === `/${cat.slug}/${l2.slug}/${l3.slug}`
                                    ? "text-primary bg-primary/5 font-semibold"
                                    : "text-foreground/80 hover:text-primary"
                                }`}
                              >
                                {l3.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {/* Account section */}
      <div className="pt-4 space-y-2">
        {user ? (
          <>
            <Link to="/account" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>My Account</Link>
            <Link to="/orders" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>My Orders</Link>
            <Link to="/account/returns" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>My Returns</Link>
            <Link to="/wishlist" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>Wishlist</Link>
            {isVendor && <Link to="/vendor" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>Vendor Portal</Link>}
            {!isVendor && <Link to="/vendor/application" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>Vendor Application</Link>}
            {isAdmin && <Link to="/admin" className="block text-foreground hover:text-primary text-sm font-medium py-2.5 transition-colors" onClick={onClose}>Admin Panel</Link>}
            <Button variant="outline" size="sm" className="w-full mt-4 text-xs uppercase tracking-widest font-semibold" onClick={() => { onSignOut(); onClose(); }}>Sign Out</Button>
          </>
        ) : (
          <Button size="sm" className="w-full text-xs uppercase tracking-widest font-semibold" asChild>
            <Link to="/auth" onClick={onClose}>Sign In</Link>
          </Button>
        )}
      </div>
    </nav>
  );
};

export default MobileNavMenu;
